/**
 * ProjectCard — rich grid card matching designs/Projects.html .pcard layout.
 *
 * Shows: icon tile (colored), project name, category badge, description,
 * progress bar (count label), owner avatar group, due date, status pill.
 */

import { Link } from "@tanstack/react-router";
import { Icon, type IconName } from "@/components/Icon";
import { Pill } from "@/components/Pill";
import { ProgressBar } from "@/components/ProgressBar";
import { AvatarGroup } from "@/components/AvatarGroup";
import { TagChip } from "@/components/TagChip";
import { formatDateShort } from "@/lib/format";
import { PROJECT_STATUS_LABEL, projectPillVariant } from "@/lib/projectStatus";
import { HealthBadge } from "./HealthBadge";

export type ProjectCardProject = {
  id: string;
  name: string;
  icon?: IconName | null;
  color?: string | null;
  category?: string | null;
  description?: string | null;
  completedCount?: number;
  totalCount?: number;
  members?: { id: string; name: string | null; image?: string | null }[];
  /** ISO string or unix timestamp (seconds) */
  targetDate?: string | number | null;
  status: "active" | "on_hold" | "archived";
  /** Optional kind for client-side filtering (standing / initiative) */
  kind?: "standing" | "initiative" | null;
  /** T46: project tags (chips render capped at 3 with a +n overflow) */
  tags?: { id: string; name: string; color: string }[];
  healthOverride?: "green" | "amber" | "red" | null;
  healthReason?: string | null;
};

export type ProjectCardProps = { project: ProjectCardProject };

function formatDue(due: string | number | null | undefined): string | null {
  const s = formatDateShort(due);
  return s || null;
}

export function ProjectCard({ project: p }: ProjectCardProps) {
  const total = p.totalCount ?? 0;
  const completed = p.completedCount ?? 0;
  const dueDateLabel = formatDue(p.targetDate);
  const bgColor = p.color ?? "var(--green-500)";
  const members = (p.members ?? []).map((m) => ({
    ...m,
    name: m.name ?? m.id,
  }));

  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: p.id }}
      className="pcard"
      data-testid={`project-card-${p.id}`}
    >
      {/* Header: icon tile + name + category */}
      <div className="pc-h">
        <span className="pc-ic" style={{ background: bgColor }}>
          {p.icon ? <Icon name={p.icon} size={19} /> : null}
        </span>
        <div className="pc-htext">
          <div className="pc-name">{p.name}</div>
          {p.category ? <div className="pc-cat">{p.category}</div> : null}
        </div>
      </div>

      {/* Description (always rendered for min-height parity with mockup) */}
      <p className="pc-desc">{p.description ?? ""}</p>

      {(p.tags ?? []).length > 0 ? (
        <div className="pc-tags">
          {(p.tags ?? []).slice(0, 3).map((t) => (
            <TagChip key={t.id} name={t.name} color={t.color} />
          ))}
          {(p.tags ?? []).length > 3 ? (
            <span className="t-xs t-muted">+{(p.tags ?? []).length - 3}</span>
          ) : null}
        </div>
      ) : null}

      {/* Progress — count label preserves "N/T" expectations elsewhere */}
      {total > 0 ? (
        <ProgressBar value={completed} total={total} label="count" />
      ) : null}

      {/* Footer: avatars (left) + [due date + status pill] (right) */}
      <div className="pc-foot">
        <div className="pc-owner">
          {members.length > 0 ? (
            <AvatarGroup users={members} max={3} size={20} />
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HealthBadge project={p} showLabel={false} />
          {dueDateLabel ? (
            <span className="pc-due">
              <Icon name="cal" size={12} />
              <span className="t-mono">{dueDateLabel}</span>
            </span>
          ) : null}
          <Pill variant={projectPillVariant(p.status)} dot>
            {PROJECT_STATUS_LABEL[p.status] ?? p.status}
          </Pill>
        </div>
      </div>
    </Link>
  );
}
